import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { timeout, catchError, retryWhen, delay } from 'rxjs/operators';

export interface PaginatedHttpOptions {
    headers?: HttpHeaders | Record<string, string | string[]>;
    withCredentials?: boolean;
}

export class Page<T> {
    total: number = 0; // total number of items
    currentPage: number = 1;
    pageSize: number = 25;
    data: Array<T> = []; // items for the current page
    duration: number = 0; // duration of the request in milliseconds

    maxPages() {
        return Math.ceil(this.total / this.pageSize) > 0 ? Math.ceil(this.total / this.pageSize) : 1;
    }
}

/**
 * Function to set query params and use of Paginated results
 * @param http The HttpClient Instance used to send the requests
 * @param baseUrl
 * @param urlOrFilter
 * @param httpOptions use empty object {} if unknown. Usage of null or undefined will lead to errors
 * @returns Observable<Page<T>>
 */
export function queryPaginated<T>(http: HttpClient, baseUrl: string, urlOrFilter?: object | string, httpOptions?: PaginatedHttpOptions, timeoutValue?: number): Observable<Page<T>> {
    let params = new HttpParams();
    let url = baseUrl;

    if (typeof urlOrFilter === 'string') {
        // we were given a page URL, use it
        url = urlOrFilter;
    } else if (typeof urlOrFilter === 'object') {
        // we were given filtering criteria, build the query string
        Object.keys(urlOrFilter)
            .sort()
            .forEach((key) => {
                const value = (urlOrFilter as any)[key];
                if (value !== null) {
                    params = params.set(key, value.toString());
                }
            });
    }

    const observable = http.get<Page<T>>(url, {
        params: params,
        headers: httpOptions?.headers,
        withCredentials: httpOptions?.withCredentials
    });

    if (timeoutValue) {
        return observable.pipe(
            timeout(timeoutValue),
            retryWhen((error) =>
                error.pipe(
                    delay(500),
                    timeout(timeoutValue),
                    catchError((err) => {
                        return throwError(() => new Error('Die API-Anfrage konnte leider nicht schnell genug vom Server beantwortet werden. Bitte versuche es später erneut.'));
                    })
                )
            )
        );
    }

    return observable;
}

/**
 * Function to set query params and use of Paginated results
 * @param http The HttpClient Instance used to send the requests
 * @param baseUrl
 * @param urlOrFilter
 * @param httpOptions use empty object {} if unknown. Usage of null or undefined will lead to errors
 * @returns Observable<Page<T>>
 */
export function queryPaginatedPost<T>(http: HttpClient, baseUrl: string, urlOrFilter?: object, httpOptions?: PaginatedHttpOptions, timeoutValue?: number): Observable<Page<T>> {
    const observable = http.post<Page<T>>(baseUrl, urlOrFilter, {
        headers: httpOptions?.headers,
        withCredentials: httpOptions?.withCredentials
    });

    if (timeoutValue) {
        return observable.pipe(
            timeout(timeoutValue),
            retryWhen((error) =>
                error.pipe(
                    delay(500),
                    timeout(timeoutValue),
                    catchError((err) => {
                        return throwError(() => new Error('Die API-Anfrage konnte leider nicht schnell genug vom Server beantwortet werden. Bitte versuche es später erneut.'));
                    })
                )
            )
        );
    }

    return observable;
}
